import AppTabs from 'general/components/AppTabs';
import KTFormInput, {
  KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTFormSelect from 'general/components/OtherKeenComponents/Forms/KTFormSelect';
import KTRadio, {
  KTRadioType,
} from 'general/components/OtherKeenComponents/KTFormControls/KTRadio';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import AppColors from 'general/constants/AppColors';
import miscHelper from 'general/helpers/miscHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { thunkGetAppConfig } from 'app/appSlice';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const TAB_EVN = 'Giá điện mua vào (EVN)';
const TAB_HOTLINE = 'Hotline trạm sạc';

GeneralSettingView.propTypes = {
  showFullUI: PropTypes.bool,
  prefKey: PropTypes.string,
};

function GeneralSettingView({ showFullUI, prefKey }) {
  const dispatch = useDispatch();
  const config = useSelector((state) => state.app.appConfig?.config);
  const operatorList = useSelector((state) => state.app.appConfig?.operators) ?? [];
  const operatorOptions = operatorList.map((item) => ({ name: item.name, value: item.operatorId }));

  const [selectedOperatorId, setSelectedOperatorId] = useState('');

  useEffect(() => {
    if (operatorList.length > 0 && !selectedOperatorId) {
      setSelectedOperatorId(operatorList[0].operatorId);
    }
  }, [operatorList]);

  const [activeTab, setActiveTab] = useState(TAB_EVN);
  const [inputPricingMode, setInputPricingMode] = useState('HOURS');
  const [inputFlatPrice, setInputFlatPrice] = useState('');
  const [inputNormalPrice, setInputNormalPrice] = useState('');
  const [inputPeakPrice, setInputPeakPrice] = useState('');
  const [inputOffPeakPrice, setInputOffPeakPrice] = useState('');
  const defaultTiered = [
    { upto: 50, price: 0 },
    { upto: 100, price: 0 },
    { upto: 200, price: 0 },
    { upto: 300, price: 0 },
    { upto: 400, price: 0 },
    { upto: 999999999, price: 0 },
  ];
  const [inputTieredPrice, setInputTieredPrice] = useState(defaultTiered);
  const [hotline, setHotline] = useState('');
  const [editModeHotline, setEditModeHotline] = useState(false);
  const [hotlineSnapshot, setHotlineSnapshot] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [evnSnapshot, setEvnSnapshot] = useState(null);
  const [attempted, setAttempted] = useState(false);

  const isValidPrice = (v) => Number(v) > 0;
  const isTieredValid = (tiers) =>
    tiers.every((t, i) => isValidPrice(t.price) && (i === 0 || Number(t.price) >= Number(tiers[i - 1].price)));

  const applyEvnConfig = (evnConfig) => {
    setInputPricingMode(evnConfig.inputPricingMode ?? 'HOURS');
    setInputFlatPrice(evnConfig.inputFlatPrice ?? '');
    setInputNormalPrice(evnConfig.inputNormalPrice ?? '');
    setInputPeakPrice(evnConfig.inputPeakPrice ?? '');
    setInputOffPeakPrice(evnConfig.inputOffPeakPrice ?? '');
    if (evnConfig.inputTieredPrice) setInputTieredPrice(evnConfig.inputTieredPrice);
  };

  useEffect(() => {
    if (!config || !selectedOperatorId) return;
    const findVal = (key) => _.find(config, (item) => item.key === key)?.value;

    const evnRaw = findVal(`ELECTRICITY_PURCHASE_PRICE_${selectedOperatorId}`);
    let evnConfig = { inputPricingMode: 'HOURS', inputFlatPrice: '', inputNormalPrice: '', inputPeakPrice: '', inputOffPeakPrice: '' };
    if (evnRaw) {
      try { evnConfig = { ...evnConfig, ...JSON.parse(evnRaw) }; } catch (_) {}
    }
    applyEvnConfig(evnConfig);
    setEvnSnapshot(evnConfig);
    setEditMode(false);
    setAttempted(false);

    const hl = findVal(`HOTLINE_${selectedOperatorId}`) ?? '';
    setHotline(hl);
    setHotlineSnapshot(hl);
    setEditModeHotline(false);
  }, [config, selectedOperatorId]);

  const handleCancelEvn = () => {
    if (evnSnapshot) applyEvnConfig(evnSnapshot);
    setEditMode(false);
    setAttempted(false);
  };

  const [savingEvn, setSavingEvn] = useState(false);
  const handleSaveEvn = async () => {
    setAttempted(true);
    const invalid =
      (inputPricingMode === 'FLAT' && !isValidPrice(inputFlatPrice)) ||
      (inputPricingMode === 'HOURS' && (!isValidPrice(inputNormalPrice) || !isValidPrice(inputPeakPrice) || !isValidPrice(inputOffPeakPrice))) ||
      (inputPricingMode === 'TIERED' && !isTieredValid(inputTieredPrice));
    if (invalid) return;

    setSavingEvn(true);
    try {
      const evnConfig = { inputPricingMode };
      if (inputPricingMode === 'FLAT') {
        evnConfig.inputFlatPrice = inputFlatPrice;
      } else if (inputPricingMode === 'HOURS') {
        evnConfig.inputNormalPrice = inputNormalPrice;
        evnConfig.inputPeakPrice = inputPeakPrice;
        evnConfig.inputOffPeakPrice = inputOffPeakPrice;
      } else if (inputPricingMode === 'TIERED') {
        evnConfig.inputTieredPrice = inputTieredPrice;
      }
      await miscHelper.callApiUpdateConfig({
        key: `ELECTRICITY_PURCHASE_PRICE_${selectedOperatorId}`,
        value: JSON.stringify(evnConfig),
        operatorId: selectedOperatorId,
      });
      setEvnSnapshot(evnConfig);
      setEditMode(false);
      setAttempted(false);
      dispatch(thunkGetAppConfig());
      ToastHelper.showSuccess('Lưu cài đặt thành công');
    } catch (_) {
      ToastHelper.showError('Lưu cài đặt thất bại');
    }
    setSavingEvn(false);
  };

  const handleCancelHotline = () => {
    setHotline(hotlineSnapshot);
    setEditModeHotline(false);
  };

  const [savingHotline, setSavingHotline] = useState(false);
  const handleSaveHotline = async () => {
    setSavingHotline(true);
    try {
      await miscHelper.callApiUpdateConfig({
        key: `HOTLINE_${selectedOperatorId}`,
        value: hotline,
        operatorId: selectedOperatorId,
        public: 1,
      });
      setHotlineSnapshot(hotline);
      setEditModeHotline(false);
      ToastHelper.showSuccess('Lưu cài đặt thành công');
    } catch (_) {
      ToastHelper.showError('Lưu cài đặt thất bại');
    }
    setSavingHotline(false);
  };

  return (
    <div>
      {operatorOptions.length > 1 && (
        <div className="d-flex align-items-center gap-3 mb-4">
          {UIHelper.buildFormLabel('Nhà điều hành', false, false)}
          <div style={{ minWidth: 220 }}>
            <KTFormSelect
              name="selectedOperatorId"
              value={selectedOperatorId}
              options={operatorOptions}
              onChange={(value) => setSelectedOperatorId(Number(value))}
            />
          </div>
        </div>
      )}

      <AppTabs
        tabs={[TAB_HOTLINE, TAB_EVN]}
        activeTab={activeTab}
        setTab={setActiveTab}
        additionalClassName="mt-0 mr-3"
      />

      {activeTab === TAB_EVN && (
        <div className="pt-5 bg-white" style={{borderTopRightRadius: '10px', borderBottomLeftRadius: '10px', borderBottomRightRadius: '10px', border: '1px solid #DBDFF1', borderTop: 'unset' }}>
          <div className="d-flex align-items-center gap-3 px-4 mb-3">
            <span style={{ fontSize: '16px', fontWeight: 700, whiteSpace: 'nowrap' , color: '#494968'}}>Loại giá điện mua vào</span>
            <div className="radio-inline d-flex gap-5" style={{ opacity: editMode ? 1 : 0.5, pointerEvents: editMode ? 'auto' : 'none' }}>
              <KTRadio
                name="inputPricingModeHOURS"
                label="Theo khung giờ"
                type={KTRadioType.outline}
                additionalClassName="radio-success mb-0"
                onChange={() => setInputPricingMode('HOURS')}
                checked={inputPricingMode === 'HOURS'}
                disabled={!editMode}
              />
              {/* <KTRadio
                name="inputPricingModeTIERED"
                label="Theo bậc thang"
                type={KTRadioType.outline}
                additionalClassName="radio-success mb-0"
                onChange={() => setInputPricingMode('TIERED')}
                checked={inputPricingMode === 'TIERED'}
                disabled={!editMode}
              /> */}
              <KTRadio
                name="inputPricingModeFLAT"
                label="Đồng giá"
                type={KTRadioType.outline}
                additionalClassName="radio-success mb-0"
                onChange={() => setInputPricingMode('FLAT')}
                checked={inputPricingMode === 'FLAT'}
                disabled={!editMode}
              />
            </div>
          </div>

          <div className="row m-0 p-0 px-2">
            {inputPricingMode === 'FLAT' && (
              <div className="col-lg-4 mb-4 px-2">
                {UIHelper.buildFormLabel('Giá điện cố định', true)}
                <KTFormInput
                  type={KTFormInputType.currency}
                  unit="VNĐ/kWh"
                  placeholder="Nhập số tiền"
                  name="inputFlatPrice"
                  value={inputFlatPrice}
                  onChange={(value) => setInputFlatPrice(value)}
                  disabled={!editMode}
                  enableCheckValid
                  isTouched={attempted}
                  isValid={isValidPrice(inputFlatPrice)}
                  feedbackText="Giá điện cố định phải là số lớn hơn 0"
                />
              </div>
            )}
            {inputPricingMode === 'HOURS' && (
              <>
                <div className="col-lg-4 mb-4 px-2">
                  <div className="d-flex gap-2">
                    {UIHelper.buildFormLabel('Giá điện giờ bình thường', true, false)}
                    <KTTooltip
                    text={`<div><div>• Từ thứ 2 đến thứ 7: 04:00 - 09:30, 11:30 - 17:00, 20:00 - 22:00</div>
                          <div>• Chủ nhật: 04:00 - 22:00</div></div>`}
                    textHeader="Giờ bình thường"
                      isHtmlContent
                    >
                      <i className="fa-regular fa-info-circle" style={{ color: AppColors.info }} />
                    </KTTooltip>
                  </div>
                  <KTFormInput
                    type={KTFormInputType.currency}
                    unit="VNĐ/kWh"
                    placeholder="Nhập giá điện giờ bình thường"
                    name="inputNormalPrice"
                    value={inputNormalPrice}
                    onChange={(value) => setInputNormalPrice(value)}
                    disabled={!editMode}
                    enableCheckValid
                    isTouched={attempted}
                    isValid={isValidPrice(inputNormalPrice)}
                    feedbackText="Giá điện giờ bình thường phải là số lớn hơn 0"
                  />
                </div>
                <div className="col-lg-4 mb-4 px-2">
                  <div className="d-flex gap-2">
                    {UIHelper.buildFormLabel('Giá điện giờ cao điểm', true, false)}
                    <KTTooltip
                      text={`<div><div>Từ thứ 2 đến thứ 7: 09:30 - 11:30, 17:00 - 20:00</div>
                        <div>Chủ nhật: không có</div></div>`}
                    textHeader="Giờ cao điểm"
                      isHtmlContent
                    >
                      <i className="fa-regular fa-info-circle" style={{ color: AppColors.info }} />
                    </KTTooltip>
                  </div>
                  <KTFormInput
                    type={KTFormInputType.currency}
                    unit="VNĐ/kWh"
                    placeholder="Nhập giá điện giờ cao điểm"
                    name="inputPeakPrice"
                    value={inputPeakPrice}
                    onChange={(value) => setInputPeakPrice(value)}
                    disabled={!editMode}
                    enableCheckValid
                    isTouched={attempted}
                    isValid={isValidPrice(inputPeakPrice)}
                    feedbackText="Giá điện giờ cao điểm phải là số lớn hơn 0"
                  />
                </div>
                <div className="col-lg-4 mb-4 px-2">    
                  <div className="d-flex gap-2">
                    {UIHelper.buildFormLabel('Giá điện giờ thấp điểm', true, false)}
                    <KTTooltip
                      text={`<div>Từ 22:00 - 04:00 tất cả các ngày trong tuần</div>`}
                      textHeader="Giờ thấp điểm"
                      isHtmlContent
                    >
                      <i className="fa-regular fa-info-circle" style={{ color: AppColors.info }} />
                    </KTTooltip>
                  </div>
                  <KTFormInput
                    type={KTFormInputType.currency}
                    unit="VNĐ/kWh"
                    placeholder="Nhập giá điện giờ thấp điểm"
                    name="inputOffPeakPrice"
                    value={inputOffPeakPrice}
                    onChange={(value) => setInputOffPeakPrice(value)}
                    disabled={!editMode}
                    enableCheckValid
                    isTouched={attempted}
                    isValid={isValidPrice(inputOffPeakPrice)}
                    feedbackText="Giá điện giờ thấp điểm phải là số lớn hơn 0"
                  />
                </div>
              </>
            )}
            {inputPricingMode === 'TIERED' && (
              <>
                {inputTieredPrice.map((input, index, array) => {
                  const isLastTier = index === array.length - 1;
                  const prevUpto = index === 0 ? 0 : array[index - 1]?.upto;
                  const currentUpto = input?.upto;
                  return (
                    <div key={`gs-tiered-${index}`} className="col-lg-4 mb-4 px-2">
                      {UIHelper.buildFormLabel(
                        `Giá điện bậc ${index + 1} (Từ ${prevUpto + 1}${
                          isLastTier ? ' kWh trở lên' : `-${currentUpto} kWh`
                        })`,
                        true
                      )}
                      <KTFormInput
                        type={KTFormInputType.currency}
                        unit="VNĐ/kWh"
                        placeholder="Nhập số tiền"
                        name={`inputTieredPrice_${index}`}
                        value={input.price}
                        onChange={(value) => {
                          const newTiers = [...inputTieredPrice];
                          newTiers[index] = { ...newTiers[index], price: value };
                          setInputTieredPrice(newTiers);
                        }}
                        disabled={!editMode}
                        enableCheckValid
                        isTouched={attempted}
                        isValid={
                          isValidPrice(input.price) &&
                          (index === 0 || Number(input.price) >= Number(inputTieredPrice[index - 1]?.price))
                        }
                        feedbackText={
                          !isValidPrice(input.price)
                            ? 'Giá phải lớn hơn 0'
                            : index > 0 && Number(input.price) < Number(inputTieredPrice[index - 1]?.price)
                              ? `Giá tại mức thứ ${index + 1} phải lớn hơn hoặc bằng mức trước`
                              : ''
                        }
                      />
                    </div>
                  );
                })}
              </>
            )}
          </div>

          <div className="d-flex justify-content-end gap-2 px-4 pb-4" style={{ paddingTop: '12px' }}>
            {editMode ? (
              <>
                {UIHelper.buildNegativeButton('Hủy bỏ', handleCancelEvn)}
                {UIHelper.buildGreenButton('Lưu thay đổi', handleSaveEvn, null, false, savingEvn)}
              </>
            ) : (
              UIHelper.buildBlueButton('Chỉnh sửa', () => setEditMode(true))
            )}
          </div>
        </div>
      )}

      {activeTab === TAB_HOTLINE && (
        <div className="pt-5 bg-white" style={{borderTopRightRadius: '10px', borderBottomLeftRadius: '10px', borderBottomRightRadius: '10px', border: '1px solid #DBDFF1', borderTop: 'unset' }}>
          <div className="row m-0 p-0 px-2">
            <div className="col-lg-4 mb-4 px-2">
              <div className="d-flex gap-2 mb-1">
                {UIHelper.buildFormLabel('Số hotline trạm sạc', true, false)}
                <KTTooltip
                  text="Số điện thoại hotline được hiển thị trên tất cả các trạm sạc trong App tài xế."
                  textHeader="Số hotline trạm sạc"
                >
                  <i className="fa-regular fa-info-circle" style={{ color: AppColors.info }} />
                </KTTooltip>
              </div>
              <KTFormInput
                type={KTFormInputType.telephone}
                placeholder="Nhập số hotline"
                name="stationHotline"
                value={hotline}
                onChange={(value) => setHotline(value)}
                disabled={!editModeHotline}
              />
            </div>
          </div>
          <div className="d-flex justify-content-end gap-2 px-4 pb-4" style={{ paddingTop: '12px' }}>
            {editModeHotline ? (
              <>
                {UIHelper.buildNegativeButton('Hủy bỏ', handleCancelHotline)}
                {UIHelper.buildGreenButton('Lưu thay đổi', handleSaveHotline, null, false, savingHotline)}
              </>
            ) : (
              UIHelper.buildBlueButton('Chỉnh sửa', () => setEditModeHotline(true))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default GeneralSettingView;
 